import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronLeft, ChevronRight, Calendar, Eye, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, isBefore, parseISO } from 'date-fns';
import HeaderNav from '@/components/HeaderNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CalendarView() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leadsData, setLeadsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showLeadsDialog, setShowLeadsDialog] = useState(false);

  const isSuperAdmin = user?.user_role === 'Super Administrator';

  // Fetch leads for the current month
  const loadCalendarData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      
      const response = await axios.get(`${API}/leads/calendar`, {
        params: { start_date: start, end_date: end },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Group leads by date
      const grouped = {};
      response.data.forEach(lead => {
        const date = lead.next_call_date;
        if (date) {
          if (!grouped[date]) {
            grouped[date] = [];
          }
          grouped[date].push(lead);
        }
      });
      
      setLeadsData(grouped);
    } catch (error) {
      console.error('Failed to load calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarData();
  }, [currentDate]);

  // Get days in current month
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get day of week for first day (0 = Sunday, 1 = Monday, etc.)
  const startDayOfWeek = monthStart.getDay();

  // Create array with empty cells for days before month starts
  const calendarDays = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    calendarDays.push(null);
  }
  calendarDays.push(...daysInMonth);

  // Navigate months
  const previousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  // Handle date click
  const handleDateClick = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const leads = leadsData[dateStr] || [];
    
    if (leads.length > 0) {
      setSelectedDate(date);
      setSelectedLeads(leads);
      setShowLeadsDialog(true);
    }
  };

  // Get color class for date
  const getDateColorClass = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const hasLeads = leadsData[dateStr]?.length > 0;
    
    if (!hasLeads) return '';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    
    if (isToday(date)) {
      return 'bg-green-100 border-green-500 text-green-800';
    } else if (isBefore(compareDate, today)) {
      return 'bg-red-100 border-red-500 text-red-800';
    } else {
      return 'bg-blue-100 border-blue-500 text-blue-800';
    }
  };

  const statusColors = {
    'New': 'bg-blue-100 text-blue-700',
    'Attempted - 1': 'bg-yellow-100 text-yellow-700',
    'Attempted - 2': 'bg-yellow-100 text-yellow-700',
    'Attempted - 3': 'bg-yellow-100 text-yellow-700',
    'Follow up': 'bg-orange-100 text-orange-700',
    'Site Visit Planned': 'bg-purple-100 text-purple-700',
    'Site Visit Done': 'bg-indigo-100 text-indigo-700',
    'Booked': 'bg-green-100 text-green-700',
    'Lost': 'bg-red-100 text-red-700',
    'Unqualified': 'bg-slate-100 text-slate-700',
    'Dropped after site visit': 'bg-slate-100 text-slate-700'
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <HeaderNav title="Lead Calendar" />
      
      <div className="container mx-auto p-6">
        {/* Month Navigation */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={previousMonth}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-bold text-slate-800">
                  {format(currentDate, 'MMMM yyyy')}
                </h2>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Today
                </Button>
              </div>
              
              <Button variant="outline" onClick={nextMonth}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Color Legend */}
        <div className="flex gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-100 border border-red-500"></div>
            <span>Overdue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-100 border border-green-500"></div>
            <span>Today</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-100 border border-blue-500"></div>
            <span>Upcoming</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <Card>
          <CardContent className="p-4">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading calendar...</div>
              </div>
            ) : (
              <>
                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center font-semibold text-slate-600 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, index) => {
                    if (!day) {
                      return <div key={`empty-${index}`} className="h-24 bg-slate-50 rounded"></div>;
                    }
                    
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const leadsForDay = leadsData[dateStr] || [];
                    const colorClass = getDateColorClass(day);
                    
                    return (
                      <div
                        key={dateStr}
                        className={`h-24 border rounded p-2 cursor-pointer transition-all hover:shadow-md ${
                          colorClass || 'bg-white'
                        } ${!isSameMonth(day, currentDate) ? 'opacity-50' : ''}`}
                        onClick={() => handleDateClick(day)}
                      >
                        <div className={`text-sm font-medium ${isToday(day) ? 'text-green-700 font-bold' : ''}`}>
                          {format(day, 'd')}
                        </div>
                        {leadsForDay.length > 0 && (
                          <div className="mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {leadsForDay.length} lead{leadsForDay.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Leads Dialog */}
      <Dialog open={showLeadsDialog} onOpenChange={setShowLeadsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Leads for {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
            </DialogTitle>
            <DialogDescription>
              {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''} scheduled for this date
            </DialogDescription>
          </DialogHeader>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedLeads.map(lead => (
                <TableRow key={lead.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{lead.customer_name}</div>
                      <div className="text-xs text-slate-500">{lead.lead_number}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <a href={`tel:${lead.mobile}`} className="flex items-center gap-1 text-cyan-600 hover:text-cyan-700">
                      <Phone className="h-3 w-3" />
                      {lead.mobile}
                    </a>
                  </TableCell>
                  <TableCell>
                    {lead.projects?.join(', ') || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-700">
                      {lead.assigned_to_name || lead.assigned_to_email || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[lead.lead_status] || 'bg-slate-100'}>
                      {lead.lead_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowLeadsDialog(false);
                        navigate(`/leads/${lead.id}`);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CalendarView;
